import * as dateMath from 'date-arithmetic';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { ArticleChangeImportance, CommandResult, rng, SubscriberArticle } from '../../../amorg-common';
import { ArticleActionPlanListView } from '../../../amorg-common/DTOs/ActionPlanListView';
import { createActivityAndActionPlan, getArticleActionPlans } from '../../../api/actionPlanClientApi';
import { createArticleEvaluation, getArticle, getArticleCategoryTags, setArticleCategoryTag, updateArticleEvaluation } from '../../../api/siteArticlesClientApi';
import { Button } from '../../../designSystem/button/Button';
import { Card } from '../../../designSystem/Card';
import { colors } from '../../../designSystem/colors/color';
import { FileLinkCommentPanel } from '../../../designSystem/compositeComponents/FileLinkCommentPanel';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { Label } from '../../../designSystem/Label';
import { SingleColumnLayout } from '../../_layouts/SingleColumnLayout';
import { Link } from '../../../designSystem/Link';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { PageHeader } from '../../../designSystem/PageHeader';
import { Small } from '../../../designSystem/typography/Body';
import { appUrlBuilder } from '../../../utils/appUrlBuilder';
import { toDateString } from '../../../utils/date';
import { ArticleVersionFields } from '../../_components/articles/ArticleVersionFields';
import { ArticleVersionHeader } from '../../_components/articles/ArticleVersionHeader';
import { ProtectedPageContext } from '../../_contexts/ProtectedPageContext';
import { MemberPageWrapper } from '../../_pageWrappers/MemberPageWrapper';
import { AppendixPanel } from './_components/AppendixPanel';
import { ChangeEvaluationFields } from './_components/ChangeEvaluationFields';
import { RequirementFields } from './_components/RequirementFields';
import { PageSection } from '../../../designSystem/PageSection';

export function ArticlePage (): JSX.Element
{
    const [articleCrumb, setArticleCrumb] = React.useState<{ listName:string, listId:string, title:string } | null>(null);

    return (
        <MemberPageWrapper
            selectedMenuItemKey={'articles'}
            breadCrumbs={[
                { text:'Lovservice', url:'/articles' },
                ...(articleCrumb ? [
                    { text:articleCrumb.listName, url:`/articles/list?lid=${articleCrumb.listId}` },
                    { text:articleCrumb.title, url:'' }
                ] : [])
            ]}>
            <ArticlePageContent onArticleChanged={setArticleCrumb} />
        </MemberPageWrapper>
    )
}

type ArticlePageContentProps = {
    onArticleChanged:(article:{ listName:string, listId:string, title:string } | null) => void,
}

export function ArticlePageContent (props:ArticlePageContentProps): JSX.Element
{
    let articleId = '';
    const { articleId:paramArticleId } = useParams<{ articleId: string; }>();
    if(paramArticleId !== undefined)
        articleId = paramArticleId;

    const [loading, setLoading] = React.useState(true);
    const [articleVersions, setArticleVersions] = React.useState<SubscriberArticle[]>([]);
    const [availableCategoryTags, setAvailableCategoryTags] = React.useState<string[]>([]);
    const [articleIndex, setArticleIndex] = React.useState(0);

    React.useEffect(() =>
    {
        const getData = async () =>
        {
            const articleVersions = await getArticle(articleId);
            setArticleVersions(articleVersions);
            setAvailableCategoryTags(await getArticleCategoryTags());
            setLoading(false);
        };
        getData();
    }, []);

    async function handleVersionChange (version: number)
    {
        setLoading(true);
        setArticleIndex(articleVersions.findIndex(av => av.version === version));
        setLoading(false);
    }

    async function handleCreateEvaluation (articleVersionId: string): Promise<CommandResult>
    {
        setLoading(true);
        const result = await createArticleEvaluation(articleVersionId);
        if(!result.isSuccess){
            setLoading(false);
            return result;
        }
        const articleVersions = await getArticle(articleId);
        setArticleVersions(articleVersions);
        setLoading(false);
        return result;
    }

    async function handleUpdateEvaluation (evaluationId: string, evaluationText: string, evaluationImportance: ArticleChangeImportance | null): Promise<CommandResult>
    {
        setLoading(true);
        const result = await updateArticleEvaluation(evaluationId, evaluationText, evaluationImportance);
        if(!result.isSuccess){
            setLoading(false);
            return result;
        }
        const articleVersions = await getArticle(articleId);
        setArticleVersions(articleVersions);
        setLoading(false);
        return result;
    }

    async function handleUpdated ()
    {
        setLoading(true);
        const articleVersions = await getArticle(articleId);
        setArticleVersions(articleVersions);
        setLoading(false);
    }

    async function handleCategoryTagChange (tag:string)
    {
        setLoading(true);
        const result = await setArticleCategoryTag(articleId, tag);
        if(result.isSuccess){
            const articleVersions = await getArticle(articleId);
            setArticleVersions(articleVersions);
            setAvailableCategoryTags(await getArticleCategoryTags());
        }
        setLoading(false);
    }

    const ctx = React.useContext(ProtectedPageContext);

    const article = articleVersions.length > 0 ? articleVersions[articleIndex] : null;
    const showPublicLink = ctx.user?.features.includes('PublicArticles');

    React.useEffect(() =>
    {
        props.onArticleChanged(article ? { listName: article.list.name, listId: article.list.id, title: article.title } : null);
    }, [article]);

    return (
        <>
        <PageHeader 
            headerStyle="secondary-gray"
            headerIcon='balance'
            headerText='Lovservice'
            narrow />
        <SingleColumnLayout narrow>
            <Flex flexDirection='column' gap={16}>
                <LoadingPageOverlay loading={loading} />

                {article &&
                    <Flex flexDirection='column' gap={32}>
                        <ArticleVersionPanel key={article.articleVersionId}
                            siteId={ctx.user?.siteId || ''}
                            article={article}
                            articleIndex={articleIndex}
                            versions={articleVersions.map(av => av.version)}
                            availableCategoryTags={availableCategoryTags}
                            onVersionChange={handleVersionChange}
                            onCreateEvaluation={handleCreateEvaluation}
                            onUpdateEvaluation={handleUpdateEvaluation}
                            onUpdated={handleUpdated}
                            onCategoryTagChange={handleCategoryTagChange}
                            publicLink={showPublicLink ? { siteId: ctx.user?.siteId || '' } : undefined}
                        />
                    </Flex>
                }
            </Flex>
        </SingleColumnLayout>
        </>
    );
}

type ArticleVersionPanelProps = {
    siteId:string,
    article: SubscriberArticle,
    articleIndex: number,
    versions: number[],
    publicLink?:{siteId:string},
    availableCategoryTags:string[],
    onVersionChange: (version: number) => void,
    onCreateEvaluation: (articleVersionId: string) => Promise<CommandResult>,
    onUpdateEvaluation: (evaluationId: string, evaluationText: string, evaluationImportance: ArticleChangeImportance | null) => Promise<CommandResult>;
    onCategoryTagChange: (tag:string) => void;
    onUpdated: () => void;
};
function ArticleVersionPanel (props: ArticleVersionPanelProps): JSX.Element
{

    const latestVersion = Math.max(...props.versions);
    const isLatestVersion = props.article.version === latestVersion;

    return (
        <Flex flexDirection='column' gap={16}>


                <PageSection contentStyleVariant="card">
                    <Flex flexDirection="column" gap={32}>

                        <ArticleVersionHeader 
                            version={props.article.version} 
                            releasedDate={props.article.release?.releasedDate as Date}
                            versions={props.versions}
                            onVersionChange={props.onVersionChange}
                        />

                        <ArticleVersionFields {...props.article} 
                            importance={props.article.publication.importance}
                            publicLink={props.publicLink}
                            availableCategoryTags={props.availableCategoryTags}
                            selectedCategoryTag={props.article.categoryTag}
                            onCategoryTagChange={props.onCategoryTagChange}
                        />
                    </Flex>
                </PageSection>

                {props.article.appendices.length > 0 &&
                    <AppendixPanel appendices={props.article.appendices} />
                }


                {props.article.version === latestVersion && (props.article.requirements || props.article.customRequirements) &&                    
                    <Card variant="outlined">
                        <RequirementFields article={props.article} onUpdated={props.onUpdated} />
                    </Card>
                }

                <Card variant="outlined">
                    <ChangeEvaluationFields key={props.article.articleVersionId}
                        article={props.article}
                        latestVersion={isLatestVersion}
                        onCreateEvaluation={props.onCreateEvaluation}
                        onUpdateEvaluation={(id, text, importance) => props.onUpdateEvaluation(id, text, importance)}
                    />
                </Card>

                <ActionPlanPanel articleId={props.article.articleId} />

                <Flex flexDirection='column' gap={16}>     
                    <FileLinkCommentPanel 
                        fileReference={{ type:'Article', id:props.article.articleId }}
                        linkReference={{ type:'Article', id:props.article.articleId}}
                        commentReference={{ type:'Article', id:props.article.articleId }}

                    />
                </Flex>
        </Flex>
    );
}

type ActionPlanPanelProps = {
    articleId:string
}
function ActionPlanPanel(props:ActionPlanPanelProps):JSX.Element
{
    const [actionPlans, setActionPlans] = React.useState<ArticleActionPlanListView[]>([]);

    React.useEffect(() =>
    {
        const fetchActionPlans = async () =>
        {
            const data = await getArticleActionPlans(props.articleId);
            setActionPlans(data);
        };
        fetchActionPlans();
    }, [props.articleId]);

    async function handleClickCreateActionPlan()
    {
        const now = new Date()
        const actionPlanId = rng.getRandomId();
        const result = await createActivityAndActionPlan(actionPlanId, rng.getRandomId(), { type: 'Article', customData:{ articleId:props.articleId } }, 'Ny handleplan', now, dateMath.add(now, 1, 'month'));
        if(result.isSuccess){
            document.location.href = appUrlBuilder.actionPlan.fromArticle(actionPlanId, props.articleId);
        }
    }

    return (
        <Card variant="outlined">

            <Label>Handleplaner ({actionPlans.length})</Label>

            {actionPlans.length === 0 &&
                <Flex justifyContent='space-between' alignItems='center'>
                    <p>Der er endnu ingen handleplaner knyttet til denne artikel.</p>
                    <Button variant="outlined" text="Opret handleplan" icon="assignment_late"
                        onClick={handleClickCreateActionPlan} />
                </Flex>
            }

            {actionPlans.length > 0 &&  
                <Flex flexDirection='column' gap={16}>
                    {actionPlans.map(ap => {
                        const now = new Date();
                        const open = now > ap.start && now < ap.end;
                        return (
                            <Flex key={ap.id} justifyContent='space-between' alignItems='center'>
                                <div style={{flexGrow:1}}>
                                    <Link href={appUrlBuilder.actionPlan.fromArticle(ap.id, props.articleId)}>
                                        {ap.title}
                                    </Link>
                                </div>
                                <Flex gap={32}>
                                    <div style={{flexGrow:0, width:'160px', color:open ? colors.text.normal : colors.text.muted}}>
                                        <Small>{toDateString(ap.start) } - {toDateString(ap.end)}</Small>
                                    </div>
                                    <div style={{flexGrow:0, width:'260px'}}>
                                        <Small>
                                            {ap.actionpointStats.total === 0 
                                                ? 'ingen handlepunkter oprettet'
                                                : ap.actionpointStats.completed === ap.actionpointStats.total
                                                    ? 'alle handlepunkter fuldført'
                                                    : ap.actionpointStats.pastDeadline > 0
                                                        ? `${ap.actionpointStats.pastDeadline} handlepunkt(er) med overskreden deadline`
                                                        : `${ap.actionpointStats.completed} af ${ap.actionpointStats.total} handlepunkt(er) fuldført`
                                            }
                                        </Small>
                                    </div>
                                </Flex>
                            </Flex>
                        );
                    })}

                    <Button variant="outlined" text="Opret handleplan" icon="assignment_late"
                        onClick={handleClickCreateActionPlan} />
                </Flex>
            }
        </Card>
    );
}
